<!-- Format field of the video dialog (#seriesPicker): "None" and the formats of the project chosen for the video (e.g.
     "Super Mario 64", "Trivia"; stored as data.series, the video keeps video.seriesId), plus "+ New format": a name and a
     colour, created in that project and chosen at once (saved without redrawing, src/features/formats.ts createFormat).
     With a format chosen, "Format card" opens its card (FormatDialog.vue) above the video dialog. Formats are managed on
     the Formats page (Collections). v-model: the format id ('' = none). -->
<script setup lang="ts">
import { computed, ref } from 'vue'
import FormatMark from '../FormatMark.vue'
import NewFormatField from '../fields/NewFormatField.vue'
import { useI18n } from '../../composables/useI18n'
import { useProfileData } from '../../composables/useProfileData'
import { useDataStore } from '../../stores/data'
import { createFormat, nextSeriesColor } from '../../features/formats'
import { SERIES_COLORS } from '../../utils/color'
import { saveAppData } from '../../features/storage/save'
import { useDialogsStore } from '../../stores/dialogs'

const props = defineProps<{ modelValue: string; projectId: string }>()
const emit = defineEmits<{ 'update:modelValue': [id: string] }>()
const { t } = useI18n()
const data = useProfileData()
const store = useDataStore()

const formats = computed(() => data.value.series.filter(s => s.projectId === props.projectId))
const pill = (on: boolean) => ['inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-xs transition', on ? 'bg-brand text-white' : 'bg-darkinput text-gray-400 hover:text-gray-200']

const adding = ref(false)
const color = ref(SERIES_COLORS[0])

function start() { adding.value = true; color.value = nextSeriesColor(store.getData()) }
function cancel() { adding.value = false }
function confirm(name: string) {
  const n = name.trim()
  adding.value = false
  if (!n) return
  const d = store.getData(), r = createFormat(d, n, color.value, props.projectId)
  if (r.created) saveAppData(d, true)
  emit('update:modelValue', r.id)
}
</script>

<template>
  <div id="seriesPicker">
    <div class="flex flex-wrap items-center gap-2">
      <button type="button" :class="pill(modelValue === '')" @click="emit('update:modelValue', '')">{{ t('None') }}</button>
      <button v-for="s in formats" :key="s.id" type="button" :class="pill(modelValue === s.id)" @click="emit('update:modelValue', s.id)"><FormatMark :format="s" />{{ s.name }}</button>
      <NewFormatField v-if="adding" input-id="seriesNewName" :color="color" @pick-color="c => (color = c)" @confirm="confirm" @cancel="cancel" />
      <button v-else type="button" class="px-3 py-2 rounded-full text-xs font-bold border border-dashed border-gray-600 text-gray-400 hover:text-brand hover:border-brand" @click="start"><i class="fas fa-plus mr-1"></i>{{ t('New format') }}</button>
    </div>
    <button v-if="formats.some(s => s.id === modelValue)" type="button" class="mt-2 text-[11px] font-bold text-gray-500 hover:text-brand" @click="useDialogsStore().show('format', { id: modelValue })"><i class="fas fa-clapperboard mr-1"></i>{{ t('Format card') }}</button>
  </div>
</template>
